import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ChampionshipCategory {
  id: string;
  championship_id: string;
  name: string;
  sort_order: number;
  is_default: boolean;
}

/**
 * Manages the currently-active championship category.
 *
 * - Reads/writes `?cat=<id>` in the URL (sharable, survives refresh).
 * - Falls back to the default category when missing/invalid.
 * - Returns categories list and helpers.
 */
export function useActiveCategory(championshipId: string | null | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCat = searchParams.get("cat");

  const { data: categories = [], isLoading } = useQuery<ChampionshipCategory[]>({
    queryKey: ["championship-categories", championshipId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("championship_categories")
        .select("*")
        .eq("championship_id", championshipId!)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as ChampionshipCategory[];
    },
    enabled: !!championshipId,
  });

  const defaultCategory = useMemo(
    () => categories.find((c) => c.is_default) ?? categories[0] ?? null,
    [categories],
  );

  const storageKey = championshipId ? `activeCategory:${championshipId}` : null;
  const storedCat =
    typeof window !== "undefined" && storageKey
      ? window.sessionStorage.getItem(storageKey)
      : null;

  const activeCategory = useMemo(() => {
    if (urlCat) {
      const match = categories.find((c) => c.id === urlCat);
      if (match) return match;
    }
    if (storedCat) {
      const match = categories.find((c) => c.id === storedCat);
      if (match) return match;
    }
    return defaultCategory;
  }, [urlCat, storedCat, categories, defaultCategory]);

  // Persist active category per championship so it survives navigation between pages.
  useEffect(() => {
    if (!storageKey || !activeCategory) return;
    try {
      window.sessionStorage.setItem(storageKey, activeCategory.id);
    } catch {
      // ignore storage errors
    }
  }, [storageKey, activeCategory]);

  // If URL has an invalid cat id, clear it (don't replace with default — keep URL clean).
  useEffect(() => {
    if (!urlCat || categories.length === 0) return;
    const exists = categories.some((c) => c.id === urlCat);
    if (!exists) {
      const next = new URLSearchParams(searchParams);
      next.delete("cat");
      setSearchParams(next, { replace: true });
    }
  }, [urlCat, categories, searchParams, setSearchParams]);

  const setActiveCategory = useCallback(
    (id: string) => {
      if (storageKey) {
        try {
          window.sessionStorage.setItem(storageKey, id);
        } catch {
          // ignore
        }
      }
      const next = new URLSearchParams(searchParams);
      // Don't pollute URL when selecting the default category
      if (defaultCategory && id === defaultCategory.id) {
        next.delete("cat");
      } else {
        next.set("cat", id);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, defaultCategory, storageKey],
  );

  return {
    categories,
    activeCategory,
    activeCategoryId: activeCategory?.id ?? null,
    setActiveCategory,
    isLoading,
    hasMultiple: categories.length > 1,
  };
}